import React, {useReducer} from 'react';
import {GetServerSideProps} from 'next';
import {ssrOutletStore, PageOutletStoreComp} from '@graphql/generated/page';
import {AvailableStatus, useGetStoreDetailsQuery} from '@graphql/generated/graphql';
import {BackBar, Cart, OutletStoreMenuItem, ShareButton, StoreBestSellings, StoreDetailsModal, StoreHeader} from '@components/index';
import {useMixOrderStore, usePersistentStore} from 'stores';
import {BANNER_HEIGHT} from 'modules';

export const getServerSideProps: GetServerSideProps = async ctx => {
  const {storeFriendlyPath} = ctx.query;
  const {props} = await ssrOutletStore.getServerPage({variables: {friendlyPath: decodeURI(storeFriendlyPath as string)}}, ctx);
  const {
    data: {store},
  } = props;
  if (!store) {
    return {
      redirect: {
        destination: '/',
        permanent: true,
      },
    };
  }
  return {props};
};

const Store: PageOutletStoreComp = ({
  data: {
    store: {
      friendlyPath,
      outlet: {friendlyPath: outletFriendlyPath},
      id,
      name,
      country,
      banner,
      active,
      menuSection,
    },
  },
}) => {
  const preorderDate = usePersistentStore(s => s.preorderDate);
  const mixOrderInput = useMixOrderStore(s => s.getMixOrderInput(outletFriendlyPath));
  const [showDetailsModal, toggleDetailsModal] = useReducer(s => !s, false);

  const {data} = useGetStoreDetailsQuery({
    variables: {friendlyPath: friendlyPath},
    fetchPolicy: 'cache-and-network',
  });

  if (!menuSection || !data) {
    return null;
  }

  const {menuCategories} = menuSection;
  const store = mixOrderInput?.stores?.find(({storeId}) => storeId === id);
  const backUrl = preorderDate ? `/outlets/${outletFriendlyPath}/preorder` : `/outlets/${outletFriendlyPath}`;
  const isActive = preorderDate ? true : active;
  const hasCart = !!mixOrderInput?.stores?.length;

  return (
    <section className="pb-12">
      <BackBar isTransparent={true} noShadow={true} backUrl={backUrl} />
      <ShareButton />
      <div className={`flex justify-center items-center overflow-hidden ${BANNER_HEIGHT}`}>
        <img src={banner ?? '/image-placeholder.jpg'} alt={name} className={`bg-white object-cover h-full ${banner ? '' : 'w-full'}`} />
      </div>
      <StoreHeader data={data} active={isActive} outletName={name} handleShowDetailsModal={toggleDetailsModal} />
      <StoreDetailsModal data={data} visible={showDetailsModal} outletName={name} onClose={toggleDetailsModal} />
      <StoreBestSellings store={store} country={country} isActive={isActive} menuCategories={menuCategories} />
      {menuCategories.map(({id, name, availableStatus, menuItems}) => {
        const storeAndCategoryActive = isActive && availableStatus === AvailableStatus.Available;
        return (
          <div key={id} className="paddingHorizontal pt-7">
            <h2 className="poppins-bold font-lg tracking-wide">{name}</h2>
            <div className="flex flex-col">
              {menuItems.map(menuItem => {
                const items = store?.menuItems?.filter(({menuItemId}) => menuItemId === menuItem.id) || [];
                const itemCount = items.reduce((acc, {count}) => acc + count, 0);
                return (
                  <OutletStoreMenuItem
                    key={menuItem.id}
                    country={country}
                    storeAndCategoryActive={storeAndCategoryActive}
                    itemCount={itemCount}
                    item={menuItem}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      {hasCart && <Cart.Bar />}
    </section>
  );
};

export default Store;
